import React, { useCallback, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { amsPlantListColumns } from "./columns";
import { Box, Tab, Tabs } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { amsTabs } from "./AmsTabs/ApsTabsConfig";
import { AMS_TAB_LABEL, getHash, setHash } from "./AmsTabs/hashFunctions";
import { secondAxios } from "../../../api/axios";
import { useAuthContext } from "../../../context/authContext";
import { AmsModuleDataProvider } from "./context/AmsModuleDataContext";

function AmsProjectsTable() {
  const [plants, setPlants] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });

  const { user } = useAuthContext();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!user?.client) {
      return;
    }

    async function fetchPlants() {
      try {
        setState((prev) => ({ ...prev, isLoading: true, isError: false }));

        const res = await secondAxios.get("/plant");
        const clientName = process.env.REACT_APP_CLIENT_NAME;
        console.log("res", res.data);
        const filtered =
          user.client === clientName
            ? res.data.filter((plant) => plant.aimsSent === true)
            : Array.isArray(res.data)
              ? res.data.filter(
                  (plant) =>
                    plant.clientName === user.client && plant.aimsSent === true,
                )
              : [];
        console.log("filtered", filtered);

        if (filtered.length === 1) {
          navigate(`/ams/${filtered[0].id}`, { replace: true });
          return;
        }

        setPlants(filtered);
      } catch (err) {
        console.error("Error fetching plants for AMS list:", err);
        setState((prev) => ({ ...prev, isError: true }));
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    }

    fetchPlants();
  }, [user?.client]);

  return (
    <Box sx={{ p: 2, overflowX: "auto" }}>
      <MaterialReactTable
        muiTableContainerProps={{
          sx: {
            overflowX: "auto",
            maxHeight: "none",
            minWidth: "900px",
          },
        }}
        data={plants}
        columns={amsPlantListColumns}
        defaultColumn={{ minSize: 20 }}
        enableEditing={false}
        layoutMode="grid"
        enableFacetedValues
        initialState={{ showColumnFilters: true }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: "1rem",
            fontWeight: "bold",
            borderRight: "1px solid rgba(224, 224, 224, 1)",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: "1rem",
            borderRight: "1px solid rgba(224, 224, 224, 1)",
          },
        }}
        muiToolbarAlertBannerProps={
          state.isError
            ? { color: "error", children: "Error loading plant data" }
            : undefined
        }
        state={{
          isLoading: state.isLoading,
          showAlertBanner: state.isError,
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            const target = event.target;

            if (
              !target.closest(".MuiButtonBase-root") &&
              !target.closest("a")
            ) {
              navigate(`/ams/${row.original.id}`);
            }
          },
          sx: {
            cursor: "pointer",
          },
        })}
      />
    </Box>
  );
}

function getCurrentTabIndex(tabs) {
  const hashValue = getHash(AMS_TAB_LABEL);
  const tabIndex = tabs.findIndex((tab) => tab.title === hashValue);

  if (tabIndex !== -1) {
    return tabIndex;
  }

  setHash(AMS_TAB_LABEL, tabs[0].title);
  return 0;
}

function AmsProjectTabs({ projectId }) {
  const [, setRerenderKey] = useState(Math.random());

  const reRender = useCallback(() => {
    setRerenderKey(Math.random());
  }, []);

  const tabs = useMemo(() => {
    return amsTabs(reRender, projectId);
  }, [reRender, projectId]);

  const [tabState, setTabState] = useState(() => {
    const initialTab = getCurrentTabIndex(tabs);

    return {
      currentTab: initialTab,
      mountedTabs: {
        [initialTab]: true,
      },
    };
  });

  function handleChange(_, newValue) {
    setHash(AMS_TAB_LABEL, tabs[newValue].title);

    setTabState((prev) => ({
      currentTab: newValue,
      mountedTabs: {
        ...prev.mountedTabs,
        [newValue]: true,
      },
    }));
  }

  const { currentTab, mountedTabs } = tabState;

  return (
    <AmsModuleDataProvider plantId={projectId}>
      <Box flex={1}>
        <Tabs value={currentTab} onChange={handleChange}>
          {tabs.map((tab, index) => (
            <Tab
              key={tab.title}
              label={tab.title}
              sx={{
                minWidth: 200,
                maxWidth: 320,
                whiteSpace: "normal",
                textAlign: "center",
                py: 1,
              }}
            />
          ))}
        </Tabs>

        <Box>
          {tabs.map((tab, index) => {
            if (!mountedTabs[index]) {
              return null;
            }

            return (
              <Box
                key={tab.title}
                sx={{
                  display: currentTab === index ? "block" : "none",
                }}
              >
                {tab.panel}
              </Box>
            );
          })}
        </Box>
      </Box>
    </AmsModuleDataProvider>
  );
}

export function AmsPage() {
  const { id: projectId } = useParams();

  if (projectId) {
    return <AmsProjectTabs key={projectId} projectId={projectId} />;
  }

  return <AmsProjectsTable />;
}
